import type { FC } from 'react'
import sanitizeHtml from 'sanitize-html'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import type { Image as ImageProps, Item } from './data'

const ItemSection: FC<Omit<Item, `key`>> = ({ heading, description, images }) => {
  const theme = useTheme()

  return (
    <StyledSectionBase variant="2Col">
      <StyledSectionCopy>
        <CopyTextBase>
          <CopyText.Heading>{sanitizeHtml(heading)}</CopyText.Heading>
          <CopyText.Description tag="p">{description}</CopyText.Description>
        </CopyTextBase>
      </StyledSectionCopy>
      <StyledSectionContents>
        {images?.map(({ src2x, webp, webp2x, mobile, height, width, alt }: ImageProps, index) => (
          <picture key={index}>
            {mobile && (
              <>
                <source media={theme.breakpoints.down(`md`, false)} srcSet={`${mobile.src}, ${mobile.src2x} 2x`} />
                <source
                  media={theme.breakpoints.down(`md`, false)}
                  type="image/webp"
                  srcSet={`${mobile.webp}, ${mobile.webp2x} 2x`}
                />
              </>
            )}

            <source type="image/webp" srcSet={`${webp}, ${webp2x} 2x`} />
            <Image width={width} height={height} src={src2x} alt={alt || ``} loading="lazy" />
          </picture>
        ))}
      </StyledSectionContents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    justify-content: unset;
    margin: 0;
    padding: 40px 0;
    row-gap: 24px;

    ${({ theme }) => theme.breakpoints.up(`sm`)} {
      row-gap: 40px;
    }

    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      column-gap: 80px;
    }
  }
`

const StyledSectionCopy = styled(Section.Copy)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
`

const StyledSectionContents = styled(Section.Contents)`
  justify-items: start;
  display: grid;
  gap: 16px;
`

export default ItemSection
